import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createApiClient, type ApiClient } from '../apiClient';
import { createSessionStore, type SessionStore } from './session';
import { decodeJwt } from './jwt';
import { passwordLogin, refreshSession } from './authApi';
import { authenticate, enroll } from './passkey';

export type AuthStatus = 'hydrating' | 'anon' | 'authed';

export interface AdvisorUser {
  userId: string;
  email: string;
  /** Null means "signed in, but not an advisor" — every advisor route will 403. */
  firmId: string | null;
  /** Firm admins see the whole firm's book; plain advisors see only their own. */
  firmAdmin: boolean;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AdvisorUser | null;
  api: ApiClient;
  /** Raw access token — needed for the bearer-gated CSV export download. */
  accessToken(): string | null;
  signInWithPasskey(email: string): Promise<void>;
  /** Email + password, from any device. The everyday way in for a firm. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** Adopt a session the server already issued (e.g. after setting a password). */
  adoptSession(tokens: { accessToken: string; refreshToken: string; expiresIn: number }): void;
  /** First device: an OTP-verified enrollment ticket authorizes the passkey. */
  enrollPasskey(email: string, enrollmentTicket: string): Promise<void>;
  logout(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext);
  if (v === null) throw new Error('useAuth must be used within <AuthProvider>');
  return v;
}

export function AuthProvider({
  children,
  store,
}: {
  children: ReactNode;
  /** Test seam. Defaults to the real in-memory+localStorage store. */
  store?: SessionStore;
}) {
  const sessionRef = useRef<SessionStore>(store ?? createSessionStore());
  const session = sessionRef.current;

  const [status, setStatus] = useState<AuthStatus>('hydrating');
  const [user, setUser] = useState<AdvisorUser | null>(null);

  // Bare client: NO onUnauthorized. Used for the ceremony + refresh calls so a
  // 401 from /login/refresh fails cleanly instead of re-entering refresh.
  const bare = useMemo(
    () => createApiClient({ tokens: { get: () => null, set: () => {} } }),
    [],
  );

  function applyTokens(accessToken: string, refreshToken: string, expiresIn: number): boolean {
    const claims = decodeJwt(accessToken);
    if (claims === null) return false;
    session.setSession({ accessToken, refreshToken, expiresIn });
    setUser({
      userId: claims.sub,
      email: claims.email,
      firmId: claims.advisorFirmId ?? null,
      firmAdmin: (claims.roles ?? []).includes('firm_admin'),
    });
    setStatus('authed');
    return true;
  }

  function logout(): void {
    session.clear();
    setUser(null);
    setStatus('anon');
  }

  async function signInWithPasskey(email: string): Promise<void> {
    // The server mints the session INSIDE the verified WebAuthn ceremony —
    // there is no bare-userId session route to call afterward (S6c 1a).
    const { tokens } = await authenticate(email);
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  // Sign-in happens before there is a session to attach, so it uses a client
  // with no token store rather than the authenticated one below.
  const bareApi = useMemo(() => createApiClient({ tokens: { get: () => null, set: () => {} } }), []);

  async function signInWithPassword(email: string, password: string): Promise<void> {
    // Same session the WebAuthn ceremony produces — a password is a second way
    // in, not a second class of access, so nothing downstream has to care which
    // door was used.
    const tokens = await passwordLogin(bareApi, email, password);
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  function adoptSession(tokens: { accessToken: string; refreshToken: string; expiresIn: number }): void {
    if (!applyTokens(tokens.accessToken, tokens.refreshToken, tokens.expiresIn)) {
      throw new Error('Session token was unreadable.');
    }
  }

  async function enrollPasskey(email: string, enrollmentTicket: string): Promise<void> {
    await enroll(email, 'Advisor portal', enrollmentTicket);
    // Enrollment registers the credential but issues no session; sign in with it.
    await signInWithPasskey(email);
  }

  const api = useMemo(
    () =>
      createApiClient({
        tokens: session.accessStore,
        onUnauthorized: async () => {
          const refresh = session.getRefresh();
          if (refresh === null) {
            logout();
            return false;
          }
          try {
            const t = await refreshSession(bare, refresh);
            return applyTokens(t.accessToken, t.refreshToken, t.expiresIn);
          } catch {
            logout();
            return false;
          }
        },
      }),
    // session + bare are stable refs; intentionally empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Hydrate once on mount from the persisted refresh token.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const refresh = session.getRefresh();
      if (refresh === null) {
        if (!cancelled) setStatus('anon');
        return;
      }
      try {
        const t = await refreshSession(bare, refresh);
        if (!cancelled) applyTokens(t.accessToken, t.refreshToken, t.expiresIn);
      } catch {
        if (!cancelled) logout();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = {
    status,
    user,
    api,
    accessToken: () => session.accessStore.get(),
    signInWithPasskey,
    signInWithPassword,
    adoptSession,
    enrollPasskey,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
